import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  Paper,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { pdf } from '@react-pdf/renderer';
import { format } from 'date-fns';
import VehicleChecklistPDF, {
  VehicleChecklistContext,
  type ChecklistPhotoEntry,
} from './VehicleChecklistPDF';
import {
  ChecklistAnswer,
  VEHICLE_CHECKLIST_ITEMS,
  initialChecklistAnswers,
} from './vehicleChecklistItems';
import { CHECKLIST_IMAGE_ACCEPT, CHECKLIST_MAX_PHOTOS, normalizeImageToJpegDataUri } from './vehicleChecklistPhotoUtils';

export interface VehicleChecklistDialogProps {
  open: boolean;
  onClose: () => void;
  meta: VehicleChecklistContext;
}

function safeFilenamePart(s: string): string {
  return s.replace(/[/\\?%*:|"<>']/g, '-').replace(/\s+/g, '-').slice(0, 60);
}

const LOG = '[VehicleChecklist]';

type LocalPhoto = {
  id: string;
  file: File;
  previewUrl: string;
  description: string;
};

const VehicleChecklistDialog: React.FC<VehicleChecklistDialogProps> = ({ open, onClose, meta }) => {
  const [answers, setAnswers] = useState<Record<string, ChecklistAnswer>>(initialChecklistAnswers);
  const [inspectorName, setInspectorName] = useState('');
  const [inspectorRole, setInspectorRole] = useState('');
  const [location, setLocation] = useState('');
  const [generalNotes, setGeneralNotes] = useState('');
  const [photos, setPhotos] = useState<LocalPhoto[]>([]);
  const [generating, setGenerating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  /** Evita zerar fotos em reexecuções de efeito com `open===true` (ex.: Strict Mode ao abrir o modal). */
  const prevOpenRef = useRef(false);

  const visibleItems = useMemo(
    () => VEHICLE_CHECKLIST_ITEMS.filter((it) => !it.consignmentOnly || meta.isConsignado),
    [meta.isConsignado]
  );

  const revokePhotos = (list: LocalPhoto[]) => {
    list.forEach((p) => URL.revokeObjectURL(p.previewUrl));
  };

  useEffect(() => {
    const prev = prevOpenRef.current;
    prevOpenRef.current = open;

    if (open) {
      if (!prev) {
        console.log(`${LOG} modal aberto — reset de formulário + limpando fotos (abertura nova)`);
        setAnswers(initialChecklistAnswers());
        setInspectorName('');
        setInspectorRole('');
        setLocation('');
        setGeneralNotes('');
        setPhotos((p) => {
          revokePhotos(p);
          return [];
        });
      }
    } else if (prev) {
      console.log(`${LOG} modal fechado — revogando pré-visualizações e limpando fotos`);
      setPhotos((p) => {
        revokePhotos(p);
        return [];
      });
    }
  }, [open]);

  useEffect(() => {
    console.log(`${LOG} estado photos atualizado`, {
      quantidade: photos.length,
      ids: photos.map((p) => p.id),
      arquivos: photos.map((p) => ({
        nome: p.file.name,
        tipo: p.file.type || '(vazio — alguns navegadores/câmeras não mandam MIME)',
        bytes: p.file.size,
      })),
    });
  }, [photos]);

  const sections = useMemo(
    () => Array.from(new Set(visibleItems.map((i) => i.section))),
    [visibleItems]
  );

  const handleAnswerChange = (id: string, e: SelectChangeEvent<string>) => {
    const v = e.target.value as ChecklistAnswer | '';
    setAnswers((prev) => ({ ...prev, [id]: v }));
  };

  const handlePickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    /* FileList é vivo: ao limpar o value do input o mesmo objeto `files` some. Snapshot antes do reset. */
    const chosenFiles = input.files?.length ? Array.from(input.files) : [];
    input.value = '';

    console.log(`${LOG} input file change`, {
      arquivosEscolhidos: chosenFiles.length,
      photosJaNoEstado: photos.length,
      nomes: chosenFiles.map((f) => f.name),
    });
    if (chosenFiles.length === 0) return;

    const accepted = /image\/(jpeg|png|webp)/i;
    const room = CHECKLIST_MAX_PHOTOS - photos.length;
    if (room <= 0) {
      console.warn(`${LOG} limite de fotos atingido`, CHECKLIST_MAX_PHOTOS);
      alert(`Limite de ${CHECKLIST_MAX_PHOTOS} fotos. Remova algumas para adicionar outras.`);
      return;
    }

    const next: LocalPhoto[] = [];
    for (let i = 0; i < chosenFiles.length && next.length < room; i++) {
      const file = chosenFiles[i];
      const mimeOk = !file.type || accepted.test(file.type);
      if (!mimeOk) {
        console.warn(`${LOG} arquivo rejeitado pelo MIME`, { nome: file.name, type: file.type });
        alert(`${file.name}: use JPG, PNG ou WebP (fotos HEIC costumam falhar no navegador).`);
        continue;
      }
      if (!file.type) {
        console.warn(`${LOG} MIME vazio para "${file.name}" — aceito por cautela (se o preview falhar, reexporte em JPG)`);
      }
      const id = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `p-${Date.now()}-${i}`;
      next.push({
        id,
        file,
        previewUrl: URL.createObjectURL(file),
        description: '',
      });
    }
    const totalEscolhidos = chosenFiles.length;
    console.log(`${LOG} adicionando ao estado`, {
      aceitasNesteEnvio: next.length,
      descartadasOuForaDaVaga: totalEscolhidos - next.length,
      idsNovos: next.map((n) => n.id),
    });
    if (next.length > 0) setPhotos((prev) => [...prev, ...next]);
    if (totalEscolhidos > room) {
      alert(`Foram adicionadas só as primeiras ${room} fotos (máximo ${CHECKLIST_MAX_PHOTOS}).`);
    }
  };

  const removePhoto = (id: string) => {
    setPhotos((prev) => {
      const found = prev.find((p) => p.id === id);
      if (found) URL.revokeObjectURL(found.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  };

  const setPhotoDescription = (id: string, description: string) => {
    setPhotos((prev) => prev.map((p) => (p.id === id ? { ...p, description } : p)));
  };

  const handleDownload = async () => {
    setGenerating(true);
    try {
      const base = `${window.location.origin}${process.env.PUBLIC_URL || ''}`;
      const logoUrl = `${base}/assets/logos/${encodeURIComponent('MARCA - R MOTORS horizontal.png')}`;
      const generatedAtLabel = format(new Date(), 'dd/MM/yyyy HH:mm');

      /* Snapshot estável + JPEG via canvas: o motor do PDF não embute WebP em data URI de forma confiável (fotos sumiam sem erro). */
      const photosSnapshot = [...photos];
      console.log(`${LOG} Baixar PDF — snapshot antes de gerar`, {
        photosNoEstado: photos.length,
        tamanhoSnapshot: photosSnapshot.length,
        lista: photosSnapshot.map((p) => ({ id: p.id, nome: p.file.name, type: p.file.type, bytes: p.file.size })),
      });

      let photoEntries: ChecklistPhotoEntry[] = [];
      if (photosSnapshot.length) {
        try {
          photoEntries = await Promise.all(
            photosSnapshot.map(async (p, idx) => {
              console.log(`${LOG} normalizando imagem para PDF`, { idx, nome: p.file.name });
              const src = await normalizeImageToJpegDataUri(p.file);
              console.log(`${LOG} JPEG pronto`, {
                idx,
                prefixoSrc: src.slice(0, 48),
                tamanhoDataUriChars: src.length,
              });
              return {
                src,
                description: p.description.trim() || undefined,
              };
            })
          );
        } catch (err) {
          console.error(`${LOG} falha ao normalizar uma ou mais fotos`, err);
          throw err;
        }
      }

      console.log(`${LOG} Props enviadas ao react-pdf`, {
        fotoNoPDF: photoEntries.length,
        resumo: photoEntries.map((e, i) => ({
          i,
          descr: e.description ?? '(sem)',
          tamanhoSrc: e.src?.length ?? 0,
        })),
      });

      const doc = (
        <VehicleChecklistPDF
          meta={meta}
          items={visibleItems}
          answers={answers}
          generatedAtLabel={generatedAtLabel}
          inspectorName={inspectorName.trim() || undefined}
          inspectorRole={inspectorRole.trim() || undefined}
          location={location.trim() || undefined}
          generalNotes={generalNotes.trim() || undefined}
          logoUrl={logoUrl}
          photos={photoEntries}
        />
      );

      console.log(`${LOG} chamando pdf().toBlob()…`);
      const blob = await pdf(doc).toBlob();
      console.log(`${LOG} PDF gerado`, { tamanhoBlob: blob?.size ?? 0 });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ym = format(new Date(), 'yyyy-MM');
      const name = safeFilenamePart(
        `Checklist-vistoria-${meta.brand}-${meta.model}-${ym}`.trim() || 'checklist'
      );
      a.download = `${name}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      onClose();
    } catch (e) {
      console.error(e);
      alert('Não foi possível gerar o PDF. Tente novamente, reduza o tamanho das fotos ou use outro navegador.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => (!generating ? onClose() : undefined)} fullWidth maxWidth="md">
      <DialogTitle>Checklist de vistoria (PDF)</DialogTitle>
      <DialogContent dividers sx={{ pb: 2 }}>
        <Stack spacing={2}>
          <input
            ref={fileInputRef}
            type="file"
            accept={CHECKLIST_IMAGE_ACCEPT}
            multiple
            hidden
            onChange={handlePickFiles}
          />

          {meta.isConsignado ? (
            <Alert severity="info">
              Este veículo está marcado como <strong>consignado</strong>. O checklist inclui itens extras de documentação e
              conferência do consignante — preencha, anexe fotos se quiser, gere o PDF e guarde em{' '}
              <strong>Anexos / documentos</strong>.
            </Alert>
          ) : (
            <Alert severity="warning" variant="outlined">
              Preencha o que já foi conferido, acrescente fotos da vistoria (opcional), gere o PDF e anexe em{' '}
              <strong>documentos</strong>.
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary">
            Para cada linha escolha: <strong>Conforme</strong>, <strong>Não conforme</strong> ou <strong>N/A</strong>. Deixe em
            branco se ainda não avaliado.
          </Typography>

          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'action.hover',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Resumo do veículo no PDF
            </Typography>
            <Typography variant="body2">
              {meta.brand} {meta.model} {meta.year ? `(${meta.year})` : ''} · {meta.color || '—'} · {meta.plateOrId || '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Km / horas: {meta.km || '—'} · Chassi: {meta.chassis || '—'}
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Nome do responsável pela vistoria"
              fullWidth
              value={inspectorName}
              onChange={(e) => setInspectorName(e.target.value)}
              size="small"
            />
            <TextField
              label="Função / setor (opcional)"
              fullWidth
              value={inspectorRole}
              onChange={(e) => setInspectorRole(e.target.value)}
              size="small"
            />
          </Stack>
          <TextField label="Local (opcional)" fullWidth value={location} onChange={(e) => setLocation(e.target.value)} size="small" />

          <TextField
            label="Observações gerais (aparecem no PDF)"
            fullWidth
            multiline
            minRows={2}
            value={generalNotes}
            onChange={(e) => setGeneralNotes(e.target.value)}
            size="small"
          />

          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Fotos da vistoria (opcional)
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddPhotoAlternateIcon />}
                disabled={photos.length >= CHECKLIST_MAX_PHOTOS}
                onClick={() => fileInputRef.current?.click()}
              >
                Adicionar fotos
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              Até {CHECKLIST_MAX_PHOTOS} fotos (JPG, PNG ou WebP). Cada uma pode ter uma descrição opcional — aparecem em páginas
              extras no PDF.
            </Typography>
            <Stack spacing={1.5}>
              {photos.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'action.hover' }}>
                  <Typography variant="body2" color="text.secondary">
                    Nenhuma foto adicionada. Ideal para registrar avarias, documentos sobre o painel ou estado geral do veículo.
                  </Typography>
                </Paper>
              ) : (
                photos.map((p, idx) => (
                  <Paper key={p.id} variant="outlined" sx={{ p: 1.5, overflow: 'hidden' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                      <Box
                        component="img"
                        src={p.previewUrl}
                        alt=""
                        sx={{
                          width: { xs: '100%', sm: 140 },
                          height: { xs: 160, sm: 105 },
                          objectFit: 'cover',
                          borderRadius: 1,
                          flexShrink: 0,
                        }}
                      />
                      <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Typography variant="caption" fontWeight={600}>
                            Foto {idx + 1}
                          </Typography>
                          <IconButton size="small" aria-label="Remover foto" onClick={() => removePhoto(p.id)} edge="end">
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                        <TextField
                          label="Descrição (opcional)"
                          fullWidth
                          size="small"
                          multiline
                          minRows={2}
                          value={p.description}
                          onChange={(e) => setPhotoDescription(p.id, e.target.value)}
                          placeholder="Ex.: Amassado para-choque traseiro, trinca no para-brisa…"
                        />
                      </Stack>
                    </Stack>
                  </Paper>
                ))
              )}
            </Stack>
          </Box>

          <Box sx={{ maxHeight: { xs: '50vh', sm: '420px' }, overflowY: 'auto', pr: 0.5 }}>
            {sections.map((section) => (
              <Box key={section} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                  {section}
                </Typography>
                <Stack spacing={1.25}>
                  {visibleItems
                    .filter((it) => it.section === section)
                    .map((it) => (
                      <Stack
                        key={it.id}
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        spacing={1}
                      >
                        <Typography variant="body2" sx={{ flex: 1, pt: { sm: 0.5 } }}>
                          {it.label}
                        </Typography>
                        <Box sx={{ minWidth: { xs: '100%', sm: 200 } }}>
                          <FormControl size="small" fullWidth>
                            <InputLabel id={`chk-${it.id}-label`}>Status</InputLabel>
                            <Select
                              labelId={`chk-${it.id}-label`}
                              label="Status"
                              value={answers[it.id] || ''}
                              onChange={(e) => handleAnswerChange(it.id, e)}
                            >
                              <MenuItem value="">
                                <em>Não marcado</em>
                              </MenuItem>
                              <MenuItem value="ok">Conforme</MenuItem>
                              <MenuItem value="nc">Não conforme</MenuItem>
                              <MenuItem value="na">Não se aplica</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      </Stack>
                    ))}
                </Stack>
              </Box>
            ))}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={generating}>
          Fechar
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleDownload()}
          disabled={generating}
          startIcon={generating ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {generating ? 'Gerando…' : 'Baixar PDF'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VehicleChecklistDialog;
